// Users — flat page matching the 3CX-shape Admin Console layout. One toolbar
// row with all bulk actions, one filterable table with selectable rows,
// pagination at the bottom.

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Pencil, Trash2, Upload, Download, Shield, Copy, RotateCcw,
  Search, MoreVertical, X,
} from 'lucide-react';
import { api } from '../lib/api';

const ROLE_LABELS: Record<string, string> = {
  super_admin:   'System Owner',
  reseller:      'System Owner',
  admin:         'Department Administrator',
  receptionist:  'Receptionist',
  user:          'User',
};

function roleLabel(u: any) {
  return ROLE_LABELS[u.role || 'user'] || u.role || 'User';
}

// Status dot — green if registered + enabled, red if disabled, grey otherwise.
function statusColor(u: any) {
  if (u.enabled === false || u.enabled === 'false') return 'bg-danger';
  if (u.registered) return 'bg-success';
  return 'bg-muted/60';
}

const PAGE_SIZES = [25, 50, 100, 200];

export default function Users() {
  const me = api.me();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'enabled' | 'disabled' | 'no-2fa' | 'no-phone'>('all');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [creds, setCreds] = useState<CredsResult | null>(null);
  const [importReport, setImportReport] = useState<any | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.users.list(),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users as any[]).filter(u => {
      if (filter === 'enabled'  && !(u.enabled === true || u.enabled === 'true')) return false;
      if (filter === 'disabled' &&  (u.enabled === true || u.enabled === 'true')) return false;
      if (filter === 'no-2fa'   && u.two_fa) return false;
      if (filter === 'no-phone' && u.device_model) return false;
      if (!q) return true;
      const haystack = [u.extension, u.name, u.email, u.department, u.directory_first_name, u.directory_last_name, u.device_model]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [users, search, filter]);

  const totalCount = filtered.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(page, pageCount);
  const visible = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  function togglePick(id: string) {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  }
  function togglePickAll() {
    if (picked.size > 0) setPicked(new Set());
    else setPicked(new Set(visible.map((u: any) => u.extension_uuid)));
  }

  const bulkDelete = useMutation({
    mutationFn: async () => {
      for (const id of Array.from(picked)) {
        await api.users.remove(id).catch(() => {});
      }
    },
    onSuccess: () => { setPicked(new Set()); qc.invalidateQueries({ queryKey: ['users'] }); },
  });

  const bulk2fa = useMutation({
    mutationFn: ({ enable }: { enable: boolean }) => api.users.bulk2fa(Array.from(picked), enable),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['users'] }); alert(`${r.touched} user${r.touched === 1 ? '' : 's'} updated — 2FA ${r.enabled ? 'ENABLED' : 'DISABLED'}.`); },
    onError: (e: any) => alert(`Bulk 2FA failed: ${e.message}`),
  });

  const cloneUser = useMutation({
    mutationFn: (id: string) => api.users.clone(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setCreds({ kind: 'clone', extension: r.extension, password: r.sip_password });
    },
    onError: (e: any) => alert(`Copy failed: ${e.message}`),
  });

  const resetPwd = useMutation({
    mutationFn: (id: string) => api.users.resetPassword(id),
    onSuccess: (r, id) => {
      const u = (users as any[]).find(x => x.extension_uuid === id);
      setCreds({ kind: 'reset', extension: u?.extension || '?', password: r.password });
    },
    onError: (e: any) => alert(`Reset failed: ${e.message}`),
  });

  const importCsv = useMutation({
    mutationFn: (csv: string) => api.users.importCsv(csv),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['users'] }); setImportReport(r); },
    onError: (e: any) => alert(`Import failed: ${e.message}`),
  });

  function onEditSelected() {
    if (picked.size !== 1) return alert('Pick exactly one user to edit.');
    navigate(`/users/${Array.from(picked)[0]}/edit`);
  }
  function onDeleteSelected() {
    if (picked.size === 0) return alert('Pick at least one user to delete.');
    if (!confirm(`Delete ${picked.size} user${picked.size === 1 ? '' : 's'}? This cannot be undone.`)) return;
    bulkDelete.mutate();
  }
  function onBulk2fa() {
    if (picked.size === 0) return alert('Pick at least one user.');
    const enable = confirm(`ENABLE 2FA on ${picked.size} user${picked.size === 1 ? '' : 's'}?\n\nClick Cancel to DISABLE 2FA on the same selection instead.`);
    bulk2fa.mutate({ enable });
  }
  function onCopySelected() {
    if (picked.size !== 1) return alert('Pick exactly one user to copy.');
    cloneUser.mutate(Array.from(picked)[0]);
  }
  function onResetSelected() {
    if (picked.size !== 1) return alert('Pick exactly one user to reset password for.');
    if (!confirm('Generate a new random SIP password for the selected user? The old password will stop working immediately.')) return;
    resetPwd.mutate(Array.from(picked)[0]);
  }
  function onImportClick() { fileInput.current?.click(); }
  async function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    if (!text.trim()) { alert('File is empty.'); return; }
    importCsv.mutate(text);
    e.target.value = '';
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Users</h1>
        <FilterDropdown value={filter} onChange={setFilter} />
      </div>

      {/* Toolbar */}
      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <ToolbarButton icon={<Plus size={14}/>} label="Add User" primary onClick={() => navigate('/users/new')} />
        <ToolbarButton icon={<Pencil size={14}/>} label="Edit"     onClick={onEditSelected}   disabled={picked.size !== 1} />
        <ToolbarButton icon={<Trash2 size={14}/>} label="Delete"   onClick={onDeleteSelected} disabled={picked.size === 0} />
        <ToolbarButton icon={<Upload size={14}/>} label={importCsv.isPending ? 'Importing…' : 'Import'} onClick={onImportClick} disabled={importCsv.isPending} />
        <ToolbarButton icon={<Download size={14}/>} label={`Export (${totalCount})`} onClick={() => exportCsv(filtered)} />
        <ToolbarButton icon={<Shield size={14}/>} label="2FA"      onClick={onBulk2fa}        disabled={picked.size === 0 || bulk2fa.isPending} />
        <ToolbarButton icon={<Copy size={14}/>}   label="Copy"     onClick={onCopySelected}   disabled={picked.size !== 1 || cloneUser.isPending} />
        <ToolbarButton icon={<RotateCcw size={14}/>} label="Reset" onClick={onResetSelected}  disabled={picked.size !== 1 || resetPwd.isPending} />
        <input ref={fileInput} type="file" accept=".csv,text/csv" className="hidden" onChange={onFilePicked} />

        <div className="flex-1" />

        <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1">
          <Search size={14} className="text-textSecondary" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search…"
            className="bg-transparent outline-none text-sm w-48"
          />
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8">
                <input type="checkbox"
                       checked={picked.size > 0 && picked.size === visible.length}
                       onChange={togglePickAll} />
              </th>
              <th>User</th>
              <th>Email</th>
              <th>Extension</th>
              <th>Department</th>
              <th>DIDs</th>
              <th>IP Phone</th>
              <th className="text-center">2FA</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((u: any) => {
              const id = u.extension_uuid;
              const name = u.name || `${u.directory_first_name || ''} ${u.directory_last_name || ''}`.trim() || '—';
              const isEditTarget = picked.size === 1 && picked.has(id);
              return (
                <tr key={id} className="hover:bg-elevated">
                  <td><input type="checkbox" checked={picked.has(id)} onChange={() => togglePick(id)} /></td>
                  <td>
                    <button
                      className="flex items-center gap-2 text-left hover:underline"
                      onClick={() => navigate(`/users/${id}/edit`)}
                    >
                      <span className={`inline-block w-2.5 h-2.5 rounded-sm ${statusColor(u)}`} />
                      <span className="font-medium">{name}</span>
                      <span className="text-textSecondary text-xs">({roleLabel(u)})</span>
                      {isEditTarget && <Pencil size={10} className="text-primary" />}
                    </button>
                  </td>
                  <td className="text-sm">{u.email || ''}</td>
                  <td className="font-mono text-sm">{u.extension}</td>
                  <td className="text-sm">{u.department || ''}</td>
                  <td className="text-sm">{Array.isArray(u.dids) && u.dids.length > 0 ? u.dids.join(', ') : ''}</td>
                  <td className="text-sm">
                    {u.device_model && (
                      <span className="inline-flex items-center gap-1">
                        <span className={`inline-block w-2.5 h-2.5 rounded-sm ${statusColor(u)}`} />
                        {u.device_model} <span className="text-textSecondary">(Normal Phone)</span>
                      </span>
                    )}
                  </td>
                  <td className="text-center">
                    {u.two_fa && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-success/20 text-success text-xs font-bold">✓</span>}
                  </td>
                  <td>
                    <button className="btn-ghost p-1" title="Actions" onClick={() => navigate(`/users/${id}/edit`)}>
                      <MoreVertical size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-textSecondary">
                {totalCount === 0 ? 'No users yet.' : 'No users match the filter.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-center gap-3 text-sm">
        <button
          className="btn-ghost px-3 py-1 disabled:opacity-40"
          disabled={safePage === 1}
          onClick={() => setPage(p => Math.max(1, p - 1))}
        >Previous</button>
        {pageNumbers(safePage, pageCount).map((n, i) =>
          n === '…' ? (
            <span key={i} className="px-2 text-textSecondary">…</span>
          ) : (
            <button
              key={i}
              className={`px-3 py-1 rounded-md ${n === safePage ? 'bg-primary text-white' : 'hover:bg-elevated'}`}
              onClick={() => setPage(n as number)}
            >{n}</button>
          )
        )}
        <button
          className="btn-ghost px-3 py-1 disabled:opacity-40"
          disabled={safePage === pageCount}
          onClick={() => setPage(p => Math.min(pageCount, p + 1))}
        >Next</button>
        <select
          className="input w-20 ml-2"
          value={pageSize}
          onChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
        >
          {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>

      {showNew && me?.domain_uuid && (
        <NewUserModal domain_uuid={me.domain_uuid} onClose={() => setShowNew(false)}
                      onCreated={(extId) => { setShowNew(false); navigate(`/users/${extId}/edit`); }} />
      )}

      {creds && <CredsModal data={creds} onClose={() => setCreds(null)} />}
      {importReport && <ImportReportModal data={importReport} onClose={() => setImportReport(null)} />}
    </div>
  );
}

type CredsResult = { kind: 'clone' | 'reset'; extension: string; password: string };

function CredsModal({ data, onClose }: { data: CredsResult; onClose: () => void }) {
  const title = data.kind === 'clone' ? 'User copied' : 'Password reset';
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-md w-full p-6 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>
        <p className="text-sm text-textSecondary">
          {data.kind === 'clone'
            ? `New extension created. Hand the password below to the user — it won't be shown again.`
            : `New SIP password generated. The phone needs to be re-provisioned with this password before the extension can register.`}
        </p>
        <div className="space-y-2">
          <Field label="Extension"><div className="font-mono text-lg">{data.extension}</div></Field>
          <Field label="SIP password">
            <div className="flex gap-2">
              <code className="input font-mono">{data.password}</code>
              <button className="btn" onClick={() => navigator.clipboard.writeText(data.password)}>Copy</button>
            </div>
          </Field>
        </div>
        <div className="flex justify-end">
          <button className="btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function ImportReportModal({ data, onClose }: { data: any; onClose: () => void }) {
  const det = data.details || {};
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-lg w-full p-6 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Import complete</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <Stat n={data.created} label="Created"  cls="text-success" />
          <Stat n={data.skipped} label="Skipped"  cls="text-warning" />
          <Stat n={data.failed}  label="Failed"   cls="text-danger" />
        </div>
        {(det.skipped?.length > 0 || det.failed?.length > 0) && (
          <div className="border-t border-border pt-3 space-y-2 max-h-60 overflow-auto text-xs font-mono">
            {(det.skipped || []).map((s: any, i: number) => (
              <div key={`s${i}`} className="text-warning">row {s.row}: {s.extension || '—'} — {s.reason}</div>
            ))}
            {(det.failed || []).map((f: any, i: number) => (
              <div key={`f${i}`} className="text-danger">row {f.row}: {f.extension || '—'} — {f.reason}</div>
            ))}
          </div>
        )}
        <div className="flex justify-end">
          <button className="btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function Stat({ n, label, cls }: { n: number; label: string; cls: string }) {
  return (
    <div>
      <div className={`text-2xl font-semibold ${cls}`}>{n}</div>
      <div className="text-xs text-textSecondary">{label}</div>
    </div>
  );
}

function pageNumbers(cur: number, total: number): (number | string)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | string)[] = [];
  out.push(1);
  if (cur > 3) out.push('…');
  for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) out.push(i);
  if (cur < total - 2) out.push('…');
  out.push(total);
  return out;
}

function ToolbarButton({ icon, label, onClick, disabled, primary }: {
  icon: any; label: string; onClick: () => void; disabled?: boolean; primary?: boolean;
}) {
  const cls = primary
    ? 'btn-primary text-sm inline-flex items-center gap-1'
    : 'btn-ghost text-sm inline-flex items-center gap-1 disabled:opacity-40';
  return (
    <button className={cls} onClick={onClick} disabled={disabled}>
      {icon}{label}
    </button>
  );
}

function FilterDropdown({ value, onChange }: { value: string; onChange: (v: any) => void }) {
  return (
    <select className="input w-40" value={value} onChange={e => onChange(e.target.value)}>
      <option value="all">All</option>
      <option value="enabled">Enabled only</option>
      <option value="disabled">Disabled only</option>
      <option value="no-2fa">No 2FA</option>
      <option value="no-phone">No IP phone</option>
    </select>
  );
}

function exportCsv(rows: any[]) {
  const headers = ['Extension','Name','Email','Department','DIDs','IP Phone','2FA','Enabled'];
  const escape = (v: any) => {
    if (v == null) return '';
    const s = String(v);
    return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(',')];
  for (const u of rows) {
    csv.push([
      u.extension,
      u.name || `${u.directory_first_name || ''} ${u.directory_last_name || ''}`.trim(),
      u.email || '',
      u.department || '',
      Array.isArray(u.dids) ? u.dids.join(';') : '',
      u.device_model || '',
      u.two_fa ? 'yes' : 'no',
      u.enabled === false || u.enabled === 'false' ? 'no' : 'yes',
    ].map(escape).join(','));
  }
  const blob = new Blob([csv.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `stratus-users-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function NewUserModal({ domain_uuid, onClose, onCreated }: { domain_uuid: string; onClose: () => void; onCreated: (extId: string) => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<any>({
    extension: '', first_name: '', last_name: '', email: '', mobile: '',
    password: Math.random().toString(36).slice(2, 10),
  });

  useQuery({
    queryKey: ['next-ext', domain_uuid],
    queryFn: async () => {
      const r = await api.users.nextExtension(domain_uuid);
      if (r?.next && !form.extension) setForm((f: any) => ({ ...f, extension: r.next }));
      return r;
    },
  });

  const create = useMutation({
    mutationFn: () => api.users.create({
      domain_uuid,
      extension: form.extension,
      password: form.password,
      effective_caller_id_name: `${form.first_name} ${form.last_name}`.trim(),
      effective_caller_id_number: form.extension,
      directory_first_name: form.first_name,
      directory_last_name: form.last_name,
      email: form.email,
      mobile: form.mobile,
      enabled: 'true',
    }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      onCreated(r.extension_uuid || r.user_uuid);
    },
  });

  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-lg w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">New User</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Extension *">
            <input className="input font-mono" value={form.extension} onChange={e => set('extension', e.target.value)} placeholder="1001" />
          </Field>
          <Field label="SIP password *">
            <input className="input font-mono" value={form.password} onChange={e => set('password', e.target.value)} />
          </Field>
          <Field label="First name">
            <input className="input" value={form.first_name} onChange={e => set('first_name', e.target.value)} />
          </Field>
          <Field label="Last name">
            <input className="input" value={form.last_name} onChange={e => set('last_name', e.target.value)} />
          </Field>
          <Field label="Email">
            <input className="input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
          </Field>
          <Field label="Mobile">
            <input className="input" value={form.mobile} onChange={e => set('mobile', e.target.value)} />
          </Field>
        </div>
        <p className="text-xs text-textSecondary">After creation you'll land on the User editor where you can set forwarding profiles, BLF keys, and AI agent settings.</p>
        <div className="flex justify-end gap-2">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!form.extension || !form.password || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}
